import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { getColumnsVacationDays } from "./columns";
import { useMemo, useRef, useState } from "react";
import { AlertDialogComponent, DataTable, Pagination } from "@/components/shared";
import { PaginatedVacationDaysQueryVariables, PaginatedVacationDaysDocument, VacationDaysEdge, PaginatedVacationDaysQuery, VacationDays as GraphQLVacationDays } from "@/graphql/__generated__/graphql";
import useUserStore from "@/store/shield";
import { useRemoveVacationDayMutationHook } from "@/hooks/mutations";
import { Button } from "@/components/ui/button";
import VacationDaysFilter from "./VacationDayFilter";
import AddVacationDaysDialog from "./AddVacationDayModal";

interface PaginationRef {
  triggerHandleFilterSubmit: (_newVariables: Partial<PaginatedVacationDaysQueryVariables>) => void;
}

/**
 * VacationDaysComponent component for displaying the vacation days table
 * @returns JSX element
 */
function VacationDaysComponent() {
  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["components", "common"]);
  const { removeVacationDayMutation } = useRemoveVacationDayMutationHook();

  const { isConfigAdmin } = useUserStore();

  // Pagination config
  const paginationRef = useRef<PaginationRef | null>(null);
  const paginationName = "paginatedVacationDays";

  // State management
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveVacationDaysOpen, setIsRemoveVacationDaysOpen] = useState(false);
  const [selectedVacationDays, setSelectedVacationDays] = useState<GraphQLVacationDays | null>(null);

  const onFilterSubmit = (variables: PaginatedVacationDaysQueryVariables) => {
    // When the filter is submitted in the PageComponent,
    // this will call the handleFilterSubmit inside Pagination.
    paginationRef.current?.triggerHandleFilterSubmit(variables);
  };

  const handleRemoveClick = (vacationDays: GraphQLVacationDays) => {
    setSelectedVacationDays(vacationDays);
    setIsRemoveVacationDaysOpen(true);
  };

  const handleRemoveVacationDays = (vacationDays: GraphQLVacationDays) => {
    removeVacationDayMutation({ variables: { removeVacationDaysId: vacationDays.id } }).finally(() => {
      setIsRemoveVacationDaysOpen(false);
    });
  };

  const columns = useMemo(() => getColumnsVacationDays(intl, t, handleRemoveClick, isConfigAdmin), [intl, t, handleRemoveClick, isConfigAdmin]);

  return (
    <>
      <Pagination<VacationDaysEdge, PaginatedVacationDaysQuery, PaginatedVacationDaysQueryVariables>
        ref={paginationRef} props={{ queryDocument: PaginatedVacationDaysDocument, paginationName }}>
        {(dataSlice, loading, displayAmount) => (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2 justify-end">
              <VacationDaysFilter onFilterSubmit={onFilterSubmit} displayAmount={displayAmount as number} />
              {isConfigAdmin && <Button
                onClick={() => setIsAddModalOpen(true)}
              >
                {t("common:buttons.add")}
              </Button>}
            </div>
            <DataTable
              loading={loading}
              displayAmount={displayAmount as number}
              data={dataSlice.map(edge => edge.node) ?? []}
              columns={columns}
              enableColumnPinning
              columnPinning={{
                right: ["actions"]
              }}
              className="max-h-[calc(100vh-20rem)]"
            />
          </div>
        )}
      </Pagination>


      <AddVacationDaysDialog
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
      />

      {/* Remove Vacation Days Alert Dialog */}
      <AlertDialogComponent
        open={isRemoveVacationDaysOpen}
        onOpenChange={setIsRemoveVacationDaysOpen}
        actionButtonFn={() => handleRemoveVacationDays(selectedVacationDays as GraphQLVacationDays)}
        title={t("components:alertdialogs.removetitle")}
        bodyText={t("components:alertdialogs.removevacationdays")}
      />
    </>
  );
}

export default VacationDaysComponent;
